import { ApplicationCreateRequest } from '../dto/application-create-request';
import { ApplicationCreateResponse } from '../dto/application-create-response';

/** Application result after submission */
export type ApplicationStatus = 'PENDING' | 'APPROVED' | 'DENIED';

/** OTP delivery method chosen when the application is created */
export type OtpMethod = 'EMAIL' | 'PHONE';

/**
 * Application class is used to hold important information for a user's application
 * when moving through the application
 */
export class Application {
  constructor(
    public applicationGuid: string,
    public creditLimit: string, // Initially empty, updated later if application is approved
    public status: ApplicationStatus,
    public otpMethod: OtpMethod,
    public firstName: string,
    public lastName: string,
    public email: string,
    public phone: string,
    public street: string,
    public apt: string,
    public city: string,
    public state: string,
    public zip: string,
    public dateOfBirth: string, // Stored as YYYY-MM-DD
    public ssn: string, // Stored as plain digits
    public income: number,
    public dealerId?: string, // Optional dealer id captured from URL query param
  ) {}

  /**
   * Formats the credit limit with thousand separators and dollar sign prefix
   * @returns Formatted credit limit (e.g., "$10,000")
   */
  get formattedCreditLimit(): string {
    if (!this.creditLimit) return '$0';
    const num = parseFloat(this.creditLimit);
    if (isNaN(num)) return '$0';
    return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
}

/**
 * Mapper class for converting ApplicationCreateResponse to Application
 */
export class ApplicationMapper {
  /**
   * Maps an ApplicationCreateResponse to an Application instance
   * @param response The response from the application creation API
   * @returns A new Application instance
   */
  static fromCreate(
    request: ApplicationCreateRequest,
    response: ApplicationCreateResponse,
  ): Application {
    return new Application(
      response.applicationGuid,
      response.creditLimit ?? '',
      response.status,
      response.otpMethod,
      request.firstName,
      request.lastName,
      request.email,
      request.phone,
      request.street,
      request.apt,
      request.city,
      request.state,
      request.zip,
      request.dateOfBirth,
      '', // Don't save ssn -> more security ?
      request.income,
      request.dealerId,
    );
  }
}
